import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useReactToPrint } from 'react-to-print';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete,
  Tabs, Tab, Divider, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Grid, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, ToggleButton, ToggleButtonGroup, InputAdornment, Tooltip,
  Switch, FormControlLabel
} from '@mui/material';
import {
  Receipt, Print, Add, Delete, Search, CheckCircle,
  Hotel, Spa, ContentCut, Assignment, MedicalServices, Payments,
  FilterList, Close, AccountBalance, Undo, Person, Edit
} from '@mui/icons-material';
import { patientAPI, billingAPI, ipdAPI, dayCareAPI, servicePackagesAPI } from '../../services/api';
import BillPrint from './BillPrint';
import PaymentReceipt from './PaymentReceipt';

const CONSULTATION_FEE = 200;

// ─── Helper ───────────────────────────────────────────────────────────────────
function BillTotals({ totals }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
        <Typography variant="body2">₹{totals.subtotal.toFixed(2)}</Typography>
      </Box>
      {totals.cgst > 0 && <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">CGST (2.5%):</Typography>
          <Typography variant="body2">₹{totals.cgst.toFixed(2)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">SGST (2.5%):</Typography>
          <Typography variant="body2">₹{totals.sgst.toFixed(2)}</Typography>
        </Box>
      </>}
      {totals.discount > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="error.main">
            Discount{totals.discountType === 'percent' ? ` (${totals.discountValue}%)` : ''}:
          </Typography>
          <Typography variant="body2" color="error.main">- ₹{totals.discount.toFixed(2)}</Typography>
        </Box>
      )}
      <Divider sx={{ my: 0.5 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body1" fontWeight="bold">Total:</Typography>
        <Typography variant="body1" fontWeight="bold" color="primary">
          ₹{totals.total.toFixed(2)}
        </Typography>
      </Box>
    </Box>
  );
}

function DiscountField({ value, onChange, discountType, onTypeChange }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, alignItems: 'flex-start' }}>
      <ToggleButtonGroup value={discountType} exclusive onChange={(_, v) => v && onTypeChange(v)} size="small" sx={{ flexShrink: 0 }}>
        <ToggleButton value="amount" sx={{ px: 1.5, py: 0.5, fontSize: 13 }}>₹</ToggleButton>
        <ToggleButton value="percent" sx={{ px: 1.5, py: 0.5, fontSize: 13 }}>%</ToggleButton>
      </ToggleButtonGroup>
      <TextField
        fullWidth size="small"
        label={discountType === 'percent' ? 'Discount (%)' : 'Discount (₹)'}
        type="number"
        value={value}
        onChange={e => onChange(e.target.value)}
        InputProps={{
          endAdornment: <InputAdornment position="end">{discountType === 'percent' ? '%' : '₹'}</InputAdornment>
        }}
        inputProps={{ min: 0, max: discountType === 'percent' ? 100 : undefined, step: discountType === 'percent' ? 0.5 : 1 }}
      />
    </Box>
  );
}

function paymentStatusChip(status) {
  const map = { paid: 'success', partial: 'warning', pending: 'error' };
  return <Chip label={status?.toUpperCase()} size="small" color={map[status] || 'default'} />;
}

function billTypeChip(type) {
  const labels = { opd: 'OPD', ipd: 'IPD', day_care: 'DAY CARE' };
  const colors = { opd: 'default', ipd: 'primary', day_care: 'secondary' };
  return <Chip label={labels[type] || type} size="small" color={colors[type] || 'default'} />;
}

// ─── Main Component ────────────────────────────────────────────────────────────
function BillingModule() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isFinance = user.role === 'finance';
  const isReception = user.role === 'reception';
  const canGenerate = isReception || user.role === 'admin';

  // Top-level sections: 0=Invoices, 1=Payments
  const [section, setSection] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // ─── Invoice List (both sections) ─────────────────────────────────────────
  // For reception/admin: 0=Generate, 1=All Invoices
  // For finance: Generate tab is hidden, so 0=All Invoices
  const allInvoicesTabIdx = canGenerate ? 1 : 0;
  const [invoiceTab, setInvoiceTab] = useState(0);
  const [allBills, setAllBills] = useState([]);
  const [allBillsLoading, setAllBillsLoading] = useState(false);
  const [billFilters, setBillFilters] = useState({ search: '', billType: '', paymentStatus: '', dateFrom: '', dateTo: '' });

  // ─── Generate Invoice state ────────────────────────────────────────────────
  const [generateTab, setGenerateTab] = useState(0); // 0=OPD, 1=IPD, 2=Day Care
  const [loading, setLoading] = useState(false);

  // Patient selection
  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [consultationInfo, setConsultationInfo] = useState(null);
  const [patientBenefits, setPatientBenefits] = useState(null);
  const [billingOptions, setBillingOptions] = useState({ treatments: [], packages: [] });
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [serviceSearch, setServiceSearch] = useState('');
  const [billTransactionId, setBillTransactionId] = useState('');
  const [billItems, setBillItems] = useState([]);

  // IPD
  const [activeAdmissions, setActiveAdmissions] = useState([]);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [ipdBillingData, setIpdBillingData] = useState(null);
  const [ipdBillItems, setIpdBillItems] = useState([]);

  // Day Care
  const [activeDayCareRegs, setActiveDayCareRegs] = useState([]);
  const [selectedDayCare, setSelectedDayCare] = useState(null);
  const [dayCareItems, setDayCareItems] = useState([]);
  const [dayCareAdvanceDays, setDayCareAdvanceDays] = useState('');

  // Shared payment options
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [billNotes, setBillNotes] = useState('');
  const [billDiscount, setBillDiscount] = useState('');
  const [discountType, setDiscountType] = useState('amount'); // 'amount' | 'percent'
  const [initialPayment, setInitialPayment] = useState('');

  // Walk-in mode (OPD)
  const [walkinMode, setWalkinMode] = useState(false);
  const [walkinName, setWalkinName] = useState('');
  const [walkinPhone, setWalkinPhone] = useState('');

  // Custom item form
  const [customItemForm, setCustomItemForm] = useState({ desc: '', qty: 1, price: '', taxable: false });
  const [showCustomForm, setShowCustomForm] = useState(false);

  // Refund dialog
  const [refundDialog, setRefundDialog] = useState({ open: false, bill: null });
  const [refundAmount, setRefundAmount] = useState('');
  const [refundMethod, setRefundMethod] = useState('cash');
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  // ─── Bill/Payment print dialogs ────────────────────────────────────────────
  const [billDialog, setBillDialog] = useState({ open: false, bill: null });
  const [paymentDialog, setPaymentDialog] = useState({ open: false, bill: null, payment: null });
  const billPrintRef = useRef();
  const receiptPrintRef = useRef();

  const handlePrintBill = useReactToPrint({
    contentRef: billPrintRef,
    documentTitle: 'Invoice',
    pageStyle: '@page { size: A5 landscape; margin: 6mm 8mm; }'
  });
  const handlePrintReceipt = useReactToPrint({
    contentRef: receiptPrintRef,
    documentTitle: 'Payment Receipt',
    pageStyle: '@page { size: A5 portrait; margin: 8mm; }'
  });

  // ─── Summary print refs ────────────────────────────────────────────────────
  const invoiceSummaryRef = useRef();
  const paymentSummaryRef = useRef();
  const handlePrintInvoiceSummary = useReactToPrint({
    contentRef: invoiceSummaryRef,
    documentTitle: 'Invoices Summary',
    pageStyle: '@page { size: A4 landscape; margin: 10mm; }'
  });
  const handlePrintPaymentSummary = useReactToPrint({
    contentRef: paymentSummaryRef,
    documentTitle: 'Payments Summary',
    pageStyle: '@page { size: A4 landscape; margin: 10mm; }'
  });

  // ─── Record Payment dialog ─────────────────────────────────────────────────
  const [recordPaymentDialog, setRecordPaymentDialog] = useState({ open: false, bill: null });
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethodRec, setPaymentMethodRec] = useState('cash');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentTransactionId, setPaymentTransactionId] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  // ─── Edit Bill dialog ──────────────────────────────────────────────────────
  const [editBillDialog, setEditBillDialog] = useState({ open: false, bill: null });
  const [editBillWalkinName, setEditBillWalkinName] = useState('');
  const [editBillWalkinPhone, setEditBillWalkinPhone] = useState('');
  const [editBillPaymentMethod, setEditBillPaymentMethod] = useState('cash');
  const [editBillLoading, setEditBillLoading] = useState(false);

  // ─── Edit Payment dialog ───────────────────────────────────────────────────
  const [editPaymentDialog, setEditPaymentDialog] = useState({ open: false, payment: null, bill: null });
  const [editPayAmount, setEditPayAmount] = useState('');
  const [editPayMethod, setEditPayMethod] = useState('cash');
  const [editPayTxnId, setEditPayTxnId] = useState('');
  const [editPayLoading, setEditPayLoading] = useState(false);

  // ─── All Payments list ─────────────────────────────────────────────────────
  const [allPayments, setAllPayments] = useState([]);
  const [allPaymentsLoading, setAllPaymentsLoading] = useState(false);
  const [paymentFilters, setPaymentFilters] = useState({ search: '', paymentMethod: '', dateFrom: '', dateTo: '' });
  const [paymentSummary, setPaymentSummary] = useState({ today_total: 0, today_count: 0 });

  // ─── Day Report ────────────────────────────────────────────────────────────
  const [dayReportDialog, setDayReportDialog] = useState({ open: false, date: new Date().toISOString().split('T')[0] });
  const [dayReportData, setDayReportData] = useState(null);
  const [dayReportLoading, setDayReportLoading] = useState(false);
  const dayReportRef = useRef();
  const handlePrintDayReport = useReactToPrint({
    contentRef: dayReportRef,
    documentTitle: `Billing Day Report - ${dayReportDialog.date}`,
    pageStyle: '@page { size: A4 landscape; margin: 10mm; }'
  });

  const fetchAndPrintDayReport = async () => {
    setDayReportLoading(true);
    try {
      const [billsRes, paymentsRes] = await Promise.all([
        billingAPI.getAllBills({ dateFrom: dayReportDialog.date, dateTo: dayReportDialog.date, limit: 500 }),
        billingAPI.getAllPayments({ dateFrom: dayReportDialog.date, dateTo: dayReportDialog.date, limit: 500 })
      ]);
      setDayReportData({
        bills: billsRes.data.data.bills || [],
        payments: paymentsRes.data.data.payments || [],
        date: dayReportDialog.date
      });
      setTimeout(() => handlePrintDayReport(), 300);
    } catch (err) {
      console.error('Failed to load day report', err);
    } finally {
      setDayReportLoading(false);
    }
  };

  // ── normalise patient ──────────────────────────────────────────────────────
  const normalizePatient = (p) => ({
    id: p.id,
    first_name: p.first_name || p.firstName || '',
    last_name: p.last_name || p.lastName || '',
    patient_code: p.patient_code || p.patientCode || '',
    phone: p.phone || '',
    gender: p.gender || '',
    date_of_birth: p.date_of_birth || p.dateOfBirth || null
  });

  // ── Initial loads ──────────────────────────────────────────────────────────
  useEffect(() => {
    billingAPI.getBillingOptions().then(r => setBillingOptions(r.data.data)).catch(() => {});
    patientAPI.getAll({ limit: 50 }).then(r => setPatientOptions((r.data.data || []).map(normalizePatient))).catch(() => {});
  }, []);

  useEffect(() => {
    if (generateTab === 1) {
      ipdAPI.getActiveAdmissions().then(r => setActiveAdmissions(r.data.data || [])).catch(() => {});
    }
    if (generateTab === 2) {
      dayCareAPI.getActiveRegistrations().then(r => setActiveDayCareRegs(Array.isArray(r.data.data) ? r.data.data : [])).catch(() => {});
    }
  }, [generateTab]);

  // ── Load all bills ─────────────────────────────────────────────────────────
  const loadAllBills = useCallback(async () => {
    setAllBillsLoading(true);
    try {
      const res = await billingAPI.getAllBills(billFilters);
      setAllBills(res.data.data.bills || []);
    } catch { /* silent */ }
    setAllBillsLoading(false);
  }, [billFilters]);

  useEffect(() => {
    if (invoiceTab === allInvoicesTabIdx) loadAllBills();
  }, [invoiceTab, loadAllBills]);

  // ── Load all payments ──────────────────────────────────────────────────────
  const loadAllPayments = useCallback(async () => {
    setAllPaymentsLoading(true);
    try {
      const res = await billingAPI.getAllPayments(paymentFilters);
      setAllPayments(res.data.data.payments || []);
      setPaymentSummary(res.data.data.summary || { today_total: 0, today_count: 0 });
    } catch { /* silent */ }
    setAllPaymentsLoading(false);
  }, [paymentFilters]);

  useEffect(() => {
    if (section === 1) loadAllPayments();
  }, [section, loadAllPayments]);

  // ── Patient search ─────────────────────────────────────────────────────────
  const handlePatientSearch = useCallback(async (query) => {
    if (query.length < 2) {
      patientAPI.getAll({ limit: 50 }).then(r => setPatientOptions((r.data.data || []).map(normalizePatient))).catch(() => {});
      return;
    }
    try {
      const res = await patientAPI.search(query);
      setPatientOptions((res.data.data || []).map(normalizePatient));
    } catch {}
  }, []);

  // ── OPD patient select ─────────────────────────────────────────────────────
  const handleSelectPatient = async (patient) => {
    setSelectedPatient(patient);
    setBillItems([]);
    setConsultationInfo(null);
    setPatientBenefits(null);
    if (!patient) return;
    try {
      const [feeRes, benefitsRes] = await Promise.all([
        billingAPI.checkConsultationFee(patient.id),
        servicePackagesAPI.getPatientBenefits(patient.id)
      ]);
      const info = feeRes.data.data;
      setConsultationInfo(info);
      setPatientBenefits(benefitsRes.data.data || null);
      if (!info.waived) {
        setBillItems([{
          item_type: 'consultation', service_type: 'general',
          description: 'Consultation Fee', quantity: 1,
          unit_price: CONSULTATION_FEE, tax_rate: 0, cgst: 0, sgst: 0,
          total_amount: CONSULTATION_FEE
        }]);
      }
    } catch {}
  };

  // ── Add therapy (OPD) ──────────────────────────────────────────────────────
  const addTherapyItem = (treatment) => {
    if (billItems.find(i => i.treatment_id === treatment.id && i.item_type === 'therapy')) {
      showSnackbar('Already added', 'info'); return;
    }
    const originalPrice = parseFloat(treatment.price);
    const serviceType = treatment.service_type || 'general';
    const pkg = patientBenefits?.activePackage;

    if (pkg && pkg.covered_treatment_ids?.includes(treatment.id)) {
      const isNonDeductible = pkg.non_deductible_treatment_ids?.includes(treatment.id);
      if (isNonDeductible) {
        showSnackbar(`${treatment.name} is always free with your package`, 'success');
        setBillItems(prev => [...prev, {
          item_type: 'therapy', service_type: serviceType,
          description: `${treatment.name} (Package — Always Free)`,
          quantity: 1, unit_price: 0, tax_rate: 0, cgst: 0, sgst: 0, total_amount: 0,
          original_unit_price: originalPrice, discount_applied: originalPrice,
          treatment_id: treatment.id, patient_package_id: pkg.id, package_session_used: false
        }]);
        return;
      }
      const usedInBill = billItems.filter(i => i.package_session_used).length;
      const available = (pkg.sessions_remaining || 0) - usedInBill;
      if (available > 0) {
        showSnackbar(`Free session applied — ${available - 1} session(s) left after this`, 'success');
        setBillItems(prev => [...prev, {
          item_type: 'therapy', service_type: serviceType,
          description: `${treatment.name} (Package Session — Free)`,
          quantity: 1, unit_price: 0, tax_rate: 0, cgst: 0, sgst: 0, total_amount: 0,
          original_unit_price: originalPrice, discount_applied: originalPrice,
          treatment_id: treatment.id, patient_package_id: pkg.id, package_session_used: true
        }]);
        return;
      }
      showSnackbar('Package sessions used up — charging regular price', 'warning');
    }
    const cgst = parseFloat(((originalPrice * 2.5) / 100).toFixed(2));
    const sgst = parseFloat(((originalPrice * 2.5) / 100).toFixed(2));
    setBillItems(prev => [...prev, {
      item_type: 'therapy', service_type: serviceType,
      description: treatment.name, quantity: 1,
      unit_price: originalPrice, tax_rate: 5, cgst, sgst,
      total_amount: parseFloat((originalPrice + cgst + sgst).toFixed(2)),
      treatment_id: treatment.id
    }]);
  };

  const addPackageItem = (pkg) => {
    if (billItems.find(i => i.package_id === pkg.id && i.item_type === 'package')) {
      showSnackbar('Already added', 'info'); return;
    }
    const unitPrice = parseFloat(pkg.price);
    setBillItems(prev => [...prev, {
      item_type: 'package', service_type: pkg.service_type || 'general',
      description: `${pkg.name} (Package)`, quantity: 1,
      unit_price: unitPrice, tax_rate: 0, cgst: 0, sgst: 0, total_amount: unitPrice,
      package_id: pkg.id
    }]);
  };

  // ── IPD select ─────────────────────────────────────────────────────────────
  const handleSelectAdmission = async (admission) => {
    setSelectedAdmission(admission);
    setIpdBillItems([]);
    setIpdBillingData(null);
    if (!admission) return;
    setLoading(true);
    try {
      const res = await billingAPI.getIPDBillingData(admission.id);
      const data = res.data.data;
      setIpdBillingData(data);
      const items = [];
      const daysAdmitted = Math.max(1, parseInt(data.admission.days_admitted) || 1);
      const dailyCharge = parseFloat(data.admission.daily_charge);
      items.push({
        item_type: 'room_charge', service_type: 'general',
        description: `Room Charge - ${data.admission.room_number} (${data.admission.category_name}) × ${daysAdmitted} day(s)`,
        quantity: daysAdmitted, unit_price: dailyCharge, tax_rate: 0, cgst: 0, sgst: 0,
        total_amount: parseFloat((dailyCharge * daysAdmitted).toFixed(2))
      });
      data.prescribedPackages.forEach(pkg => items.push({
        item_type: 'package', service_type: pkg.service_type || 'general',
        description: `${pkg.name} (Package)`, quantity: 1,
        unit_price: parseFloat(pkg.price), tax_rate: 0, cgst: 0, sgst: 0,
        total_amount: parseFloat(pkg.price), package_id: pkg.id
      }));
      data.chargeableTherapies.forEach(t => {
        const unitPrice = parseFloat(t.price);
        const cgst = parseFloat(((unitPrice * 2.5) / 100).toFixed(2));
        const sgst = parseFloat(((unitPrice * 2.5) / 100).toFixed(2));
        items.push({
          item_type: 'therapy', service_type: t.service_type || 'general',
          description: `${t.name} (not in package)`, quantity: 1,
          unit_price: unitPrice, tax_rate: 5, cgst, sgst,
          total_amount: parseFloat((unitPrice + cgst + sgst).toFixed(2)), treatment_id: t.id
        });
      });
      setIpdBillItems(items);
    } catch { showSnackbar('Failed to load IPD billing data', 'error'); }
    setLoading(false);
  };

  // ── Day Care select ────────────────────────────────────────────────────────
  const handleSelectDayCare = (reg) => {
    setSelectedDayCare(reg);
    setDayCareItems([]);
    setDayCareAdvanceDays('');
    setInitialPayment('');
    if (!reg) return;
    const plannedDays = Math.max(1, parseInt(reg.planned_days) || 1);
    const dailyCharge = parseFloat(reg.daily_charge) || 0;
    setDayCareItems([{
      item_type: 'day_care_charge', service_type: 'general',
      description: `Day Care Charge × ${plannedDays} day(s)`,
      quantity: plannedDays, unit_price: dailyCharge, tax_rate: 0, cgst: 0, sgst: 0,
      total_amount: parseFloat((dailyCharge * plannedDays).toFixed(2))
    }]);
  };

  // ── Calc totals ────────────────────────────────────────────────────────────
  const calcTotals = (items, discountValue = 0, dType = 'amount') => {
    let subtotal = 0, cgst = 0, sgst = 0;
    items.forEach(item => {
      const qty = parseInt(item.quantity) || 1;
      const unitPrice = parseFloat(item.unit_price) || 0;
      if (item.item_type === 'therapy' || item.tax_rate > 0) {
        const base = unitPrice * qty;
        subtotal += base;
        cgst += (base * 2.5) / 100;
        sgst += (base * 2.5) / 100;
      } else {
        subtotal += unitPrice * qty;
      }
    });
    const gross = subtotal + cgst + sgst;
    const dVal = parseFloat(discountValue) || 0;
    const disc = dType === 'percent'
      ? parseFloat(((gross * dVal) / 100).toFixed(2))
      : parseFloat(dVal.toFixed(2));
    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      cgst: parseFloat(cgst.toFixed(2)),
      sgst: parseFloat(sgst.toFixed(2)),
      discount: disc,
      discountType: dType,
      discountValue: dVal,
      total: parseFloat((gross - disc).toFixed(2))
    };
  };

  // ── Generate bill ──────────────────────────────────────────────────────────
  const handleGenerateBill = async () => {
    let patientId, billType, admissionId, items, consultationFee, consultationWaived;
    let walkinNameVal = null, walkinPhoneVal = null;

    if (generateTab === 0) {
      if (walkinMode) {
        if (!walkinName.trim()) { showSnackbar('Enter patient name for walk-in', 'error'); return; }
        walkinNameVal = walkinName.trim();
        walkinPhoneVal = walkinPhone.trim() || null;
        patientId = null;
      } else {
        if (!selectedPatient) { showSnackbar('Select a patient', 'error'); return; }
        patientId = selectedPatient.id;
      }
      billType = 'opd'; admissionId = null;
      items = billItems.filter(i => i.item_type !== 'consultation');
      consultationFee = consultationInfo && !consultationInfo.waived ? CONSULTATION_FEE : 0;
      consultationWaived = consultationInfo?.waived || false;
    } else if (generateTab === 1) {
      if (!ipdBillingData) { showSnackbar('Select an admission', 'error'); return; }
      patientId = ipdBillingData.admission.patient_id; billType = 'ipd';
      admissionId = selectedAdmission?.id; items = ipdBillItems;
      consultationFee = 0; consultationWaived = false;
    } else {
      if (!selectedDayCare) { showSnackbar('Select a day care registration', 'error'); return; }
      patientId = selectedDayCare.patient_id; billType = 'day_care';
      admissionId = selectedDayCare.id; items = dayCareItems;
      consultationFee = 0; consultationWaived = false;
    }

    if (!items.length && !consultationFee) { showSnackbar('No items to bill', 'error'); return; }

    const dVal = parseFloat(billDiscount) || 0;
    const initPay = parseFloat(initialPayment) || 0;

    setLoading(true);
    try {
      const createRes = await billingAPI.generateBill({
        patientId, walkinName: walkinNameVal, walkinPhone: walkinPhoneVal,
        billType, visitId: null, admissionId,
        consultationFee, consultationWaived, items, paymentMethod, notes: billNotes,
        discountType,
        discountValue: dVal,
        discountAmount: dVal, // server re-computes, but send as fallback
        initialPayment: initPay > 0 ? initPay : undefined,
        initialPaymentMethod: paymentMethod,
        initialPaymentTransactionId: billTransactionId.trim() || undefined
      });
      const { billId } = createRes.data.data;
      const billRes = await billingAPI.getBillWithPayments(billId);
      setBillDialog({ open: true, bill: billRes.data.data });
      showSnackbar('Invoice generated!', 'success');
      // Reset form
      setSelectedPatient(null); setBillItems([]); setConsultationInfo(null); setPatientBenefits(null);
      setSelectedAdmission(null); setIpdBillItems([]); setIpdBillingData(null);
      setSelectedDayCare(null); setDayCareItems([]); setDayCareAdvanceDays('');
      setBillNotes(''); setPaymentMethod('cash'); setBillDiscount(''); setDiscountType('amount'); setInitialPayment(''); setBillTransactionId('');
      setWalkinName(''); setWalkinPhone(''); setShowCustomForm(false);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to generate invoice', 'error');
    }
    setLoading(false);
  };

  // ── View bill ──────────────────────────────────────────────────────────────
  const handleViewBill = async (billId) => {
    try {
      const res = await billingAPI.getBillWithPayments(billId);
      setBillDialog({ open: true, bill: res.data.data });
    } catch { showSnackbar('Failed to load invoice', 'error'); }
  };

  // ── Record payment ─────────────────────────────────────────────────────────
  const openRecordPayment = (bill) => {
    const balance = parseFloat(bill.total_amount) - parseFloat(bill.amount_paid || 0);
    setRecordPaymentDialog({ open: true, bill });
    setPaymentAmount(balance.toFixed(2));
    setPaymentMethodRec('cash');
    setPaymentNotes('');
    setPaymentTransactionId('');
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      showSnackbar('Enter a valid amount', 'error'); return;
    }
    setPaymentLoading(true);
    try {
      await billingAPI.recordPayment({
        billId: recordPaymentDialog.bill.id,
        amount: parseFloat(paymentAmount),
        paymentMethod: paymentMethodRec,
        notes: paymentNotes,
        transactionId: paymentTransactionId.trim() || undefined
      });
      showSnackbar('Payment recorded!', 'success');
      setRecordPaymentDialog({ open: false, bill: null });
      setPaymentTransactionId('');

      // Load the updated bill + new payment, show receipt
      const billRes = await billingAPI.getBillWithPayments(recordPaymentDialog.bill.id);
      const updatedBill = billRes.data.data;
      const latestPayment = updatedBill.payments?.[updatedBill.payments.length - 1];
      if (latestPayment) setPaymentDialog({ open: true, bill: updatedBill, payment: latestPayment });

      // Refresh lists
      if (invoiceTab === allInvoicesTabIdx) loadAllBills();
      if (section === 1) loadAllPayments();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to record payment', 'error');
    }
    setPaymentLoading(false);
  };

  // ── Filters ────────────────────────────────────────────────────────────────
  const serviceSearchLower = serviceSearch.toLowerCase();
  const filteredTreatments = billingOptions.treatments.filter(t => {
    const typeMatch = serviceTypeFilter === 'all' ||
      (serviceTypeFilter === 'salon' && (t.service_type === 'salon' || t.service_type === 'spa')) ||
      (serviceTypeFilter === 'therapy' && t.service_type === 'therapy');
    const searchMatch = !serviceSearch || t.name.toLowerCase().includes(serviceSearchLower);
    return typeMatch && searchMatch;
  });
  const filteredPackages = billingOptions.packages.filter(p => {
    const typeMatch = serviceTypeFilter === 'all' ||
      (serviceTypeFilter === 'salon' && (p.service_type === 'salon' || p.service_type === 'spa')) ||
      serviceTypeFilter === 'therapy';
    const searchMatch = !serviceSearch || p.name.toLowerCase().includes(serviceSearchLower);
    return typeMatch && searchMatch;
  });

  // ── Add custom item ───��────────────────────────────────────────────────────
  const addCustomItem = (targetSetter) => {
    const { desc, qty, price, taxable } = customItemForm;
    if (!desc.trim() || !price || parseFloat(price) <= 0) {
      showSnackbar('Enter description and price', 'error'); return;
    }
    const unitPrice = parseFloat(price);
    const q = parseInt(qty) || 1;
    const cgst = taxable ? parseFloat(((unitPrice * 2.5) / 100).toFixed(2)) : 0;
    const sgst = taxable ? parseFloat(((unitPrice * 2.5) / 100).toFixed(2)) : 0;
    targetSetter(prev => [...prev, {
      item_type: 'custom', service_type: 'general',
      description: desc.trim(), quantity: q,
      unit_price: unitPrice, tax_rate: taxable ? 5 : 0,
      cgst, sgst,
      total_amount: parseFloat((unitPrice * q + cgst * q + sgst * q).toFixed(2))
    }]);
    setCustomItemForm({ desc: '', qty: 1, price: '', taxable: false });
    setShowCustomForm(false);
  };

  // ── Handle refund ──────────────────────────────────────────────────────────
  const handleProcessRefund = async () => {
    if (!refundAmount || parseFloat(refundAmount) <= 0) {
      showSnackbar('Enter a valid refund amount', 'error'); return;
    }
    setRefundLoading(true);
    try {
      await billingAPI.processRefund(refundDialog.bill.id, {
        amount: parseFloat(refundAmount),
        refundMethod,
        reason: refundReason
      });
      showSnackbar('Refund processed successfully', 'success');
      setRefundDialog({ open: false, bill: null });
      setRefundAmount(''); setRefundReason('');
      if (invoiceTab === allInvoicesTabIdx) loadAllBills();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Refund failed', 'error');
    }
    setRefundLoading(false);
  };

  // ── Open edit bill dialog ──────────────────────────────────────────────────
  const openEditBill = (bill) => {
    setEditBillDialog({ open: true, bill });
    setEditBillWalkinName(bill.walkin_name || '');
    setEditBillWalkinPhone(bill.walkin_phone || '');
    setEditBillPaymentMethod(bill.payment_method || 'cash');
  };

  const handleEditBill = async () => {
    setEditBillLoading(true);
    try {
      await billingAPI.editBill(editBillDialog.bill.id, {
        walkinName: editBillWalkinName || undefined,
        walkinPhone: editBillWalkinPhone || undefined,
        paymentMethod: editBillPaymentMethod
      });
      showSnackbar('Invoice updated', 'success');
      setEditBillDialog({ open: false, bill: null });
      if (invoiceTab === allInvoicesTabIdx) loadAllBills();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Update failed', 'error');
    }
    setEditBillLoading(false);
  };

  // ── Open edit payment dialog ───────────────────────────────────────────────
  const openEditPayment = (payment, bill) => {
    setEditPaymentDialog({ open: true, payment, bill });
    setEditPayAmount(parseFloat(payment.amount).toFixed(2));
    setEditPayMethod(payment.method || payment.payment_method || 'cash');
    setEditPayTxnId(payment.transaction_id || '');
  };

  const handleEditPayment = async () => {
    if (!editPayAmount || parseFloat(editPayAmount) <= 0) {
      showSnackbar('Enter a valid amount', 'error'); return;
    }
    setEditPayLoading(true);
    try {
      await billingAPI.editPayment(editPaymentDialog.payment.id, {
        amount: parseFloat(editPayAmount),
        paymentMethod: editPayMethod,
        transactionId: editPayTxnId.trim() || null
      });
      showSnackbar('Payment updated', 'success');
      setEditPaymentDialog({ open: false, payment: null, bill: null });
      if (section === 1) loadAllPayments();
      if (invoiceTab === allInvoicesTabIdx) loadAllBills();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Update failed', 'error');
    }
    setEditPayLoading(false);
  };

  const currentOpdItems = billItems;
  const currentOpdTotals = calcTotals(currentOpdItems, parseFloat(billDiscount) || 0, discountType);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <AccountBalance color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h5" fontWeight="bold">Billing & Finance</Typography>
      </Box>

      {/* Top-level: Invoices | Payments */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={section} onChange={(_, v) => setSection(v)}>
          <Tab label="Invoices" icon={<Receipt />} iconPosition="start" />
          <Tab label="Payments" icon={<Payments />} iconPosition="start" />
        </Tabs>
        <Tooltip title="Print Day-wise Billing Report">
          <Button variant="outlined" size="small" startIcon={<Print />}
            onClick={() => { setDayReportData(null); setDayReportDialog(d => ({ ...d, open: true })); }}>
            Day Report
          </Button>
        </Tooltip>
      </Box>

      {/* ═══════════════════════ INVOICES SECTION ═══════════════════════ */}
      {section === 0 && (
        <Box>
          <Tabs value={invoiceTab} onChange={(_, v) => setInvoiceTab(v)} sx={{ mb: 2 }}>
            {canGenerate && <Tab label="Generate Invoice" icon={<Add />} iconPosition="start" />}
            <Tab label="All Invoices" icon={<FilterList />} iconPosition="start" />
          </Tabs>

          {/* Generate Invoice */}
          {canGenerate && invoiceTab === 0 && (
            <Box>
              <Tabs value={generateTab} onChange={(_, v) => setGenerateTab(v)} sx={{ mb: 2 }}>
                <Tab label="OPD / Services" icon={<Assignment />} iconPosition="start" />
                <Tab label="IPD" icon={<Hotel />} iconPosition="start" />
                <Tab label="Day Care" icon={<MedicalServices />} iconPosition="start" />
              </Tabs>

              {/* ── OPD TAB ── */}
              {generateTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">Patient</Typography>
                        <FormControlLabel
                          control={<Switch size="small" checked={walkinMode} onChange={e => { setWalkinMode(e.target.checked); setSelectedPatient(null); setBillItems([]); setConsultationInfo(null); }} />}
                          label={<Typography variant="caption">Walk-in (no UHID)</Typography>}
                        />
                      </Box>
                      {walkinMode ? (
                        <Grid container spacing={1}>
                          <Grid item xs={8}>
                            <TextField fullWidth size="small" label="Patient Name *" value={walkinName}
                              onChange={e => setWalkinName(e.target.value)}
                              InputProps={{ startAdornment: <Person sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} /> }} />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField fullWidth size="small" label="Phone" value={walkinPhone}
                              onChange={e => setWalkinPhone(e.target.value.replace(/\D/g, '').slice(0, 15))} />
                          </Grid>
                        </Grid>
                      ) : (
                        <>
                          <Autocomplete
                            options={patientOptions}
                            getOptionLabel={o => `${o.first_name} ${o.last_name} (${o.patient_code})`}
                            onInputChange={(_, v) => handlePatientSearch(v)}
                            onChange={(_, v) => handleSelectPatient(v)}
                            value={selectedPatient}
                            renderInput={params => (
                              <TextField {...params} label="Search Patient" placeholder="Name or UHID"
                                InputProps={{ ...params.InputProps, startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }} />
                            )}
                          />
                          {consultationInfo && (
                            <Alert severity={consultationInfo.waived ? 'success' : 'info'} icon={consultationInfo.waived ? <CheckCircle /> : <Receipt />} sx={{ mt: 1 }}>
                              {consultationInfo.waived
                                ? `Consultation waived — last visit: ${new Date(consultationInfo.lastVisitDate).toLocaleDateString('en-IN')}`
                                : `Consultation fee: ₹${CONSULTATION_FEE}`}
                            </Alert>
                          )}
                          {patientBenefits?.activePackage && (
                            <Alert severity="success" icon={<Spa />} sx={{ mt: 1 }}>
                              <strong>Active Package:</strong> {patientBenefits.activePackage.plan_name} —{' '}
                              {patientBenefits.activePackage.sessions_remaining} free session(s). Expires: {new Date(patientBenefits.activePackage.expiry_date).toLocaleDateString('en-IN')}
                            </Alert>
                          )}
                        </>
                      )}
                    </Paper>

                    <Paper sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight="bold">Add Services</Typography>
                        <ToggleButtonGroup value={serviceTypeFilter} exclusive onChange={(_, v) => v && setServiceTypeFilter(v)} size="small">
                          <ToggleButton value="all">All</ToggleButton>
                          <ToggleButton value="salon"><ContentCut sx={{ mr: 0.5, fontSize: 16 }} />Spa & Salon</ToggleButton>
                          <ToggleButton value="therapy">Therapy</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>

                      <TextField
                        fullWidth size="small" placeholder="Search services or packages…"
                        value={serviceSearch}
                        onChange={e => setServiceSearch(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                        sx={{ mb: 1.5 }}
                      />

                      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        Individual Services (5% GST on therapy)
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {filteredTreatments.map(t => (
                          <Chip key={t.id}
                            label={`${t.name} ₹${t.price}`}
                            icon={t.service_type === 'salon' ? <ContentCut /> : t.service_type === 'spa' ? <Spa /> : undefined}
                            onClick={() => addTherapyItem(t)}
                            variant="outlined" color="primary" sx={{ cursor: 'pointer' }} />
                        ))}
                        {!filteredTreatments.length && <Typography variant="body2" color="text.secondary">No services found</Typography>}
                      </Box>

                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        Packages (Tax included)
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {filteredPackages.map(p => (
                          <Chip key={p.id} label={`${p.name} ₹${p.price}`}
                            onClick={() => addPackageItem(p)} variant="outlined" color="secondary" sx={{ cursor: 'pointer' }} />
                        ))}
                        {!filteredPackages.length && <Typography variant="body2" color="text.secondary">No packages found</Typography>}
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 2, position: 'sticky', top: 20 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Invoice Summary</Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell><strong>Description</strong></TableCell>
                              <TableCell align="right"><strong>Qty</strong></TableCell>
                              <TableCell align="right"><strong>Rate</strong></TableCell>
                              <TableCell align="right"><strong>GST</strong></TableCell>
                              <TableCell align="right"><strong>Total</strong></TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentOpdItems.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ maxWidth: 130, wordBreak: 'break-word', fontSize: 12 }}>{item.description}</TableCell>
                                <TableCell align="right">{item.quantity}</TableCell>
                                <TableCell align="right">₹{item.unit_price}</TableCell>
                                <TableCell align="right">{item.tax_rate > 0 ? `₹${(item.cgst + item.sgst).toFixed(2)}` : '—'}</TableCell>
                                <TableCell align="right">₹{item.total_amount.toFixed(2)}</TableCell>
                                <TableCell>
                                  <IconButton size="small" onClick={() => setBillItems(prev => prev.filter((_, i) => i !== idx))}>
                                    <Delete fontSize="small" color="error" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                            {!currentOpdItems.length && (
                              <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary">Select a patient to start</Typography></TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Custom item form */}
                      {showCustomForm ? (
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px dashed grey' }}>
                          <Typography variant="caption" fontWeight="bold" display="block" mb={1}>Custom Item</Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <TextField fullWidth size="small" label="Description *" value={customItemForm.desc}
                                onChange={e => setCustomItemForm(f => ({ ...f, desc: e.target.value }))} />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField fullWidth size="small" label="Qty" type="number" value={customItemForm.qty}
                                onChange={e => setCustomItemForm(f => ({ ...f, qty: e.target.value }))} inputProps={{ min: 1 }} />
                            </Grid>
                            <Grid item xs={5}>
                              <TextField fullWidth size="small" label="Unit Price ₹ *" type="number" value={customItemForm.price}
                                onChange={e => setCustomItemForm(f => ({ ...f, price: e.target.value }))} />
                            </Grid>
                            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                              <FormControlLabel control={<Switch size="small" checked={customItemForm.taxable}
                                onChange={e => setCustomItemForm(f => ({ ...f, taxable: e.target.checked }))} />}
                                label={<Typography variant="caption">GST</Typography>} sx={{ m: 0 }} />
                            </Grid>
                          </Grid>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button size="small" variant="contained" onClick={() => addCustomItem(setBillItems)}>Add</Button>
                            <Button size="small" onClick={() => setShowCustomForm(false)}>Cancel</Button>
                          </Box>
                        </Box>
                      ) : (
                        <Button size="small" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => setShowCustomForm(true)}>
                          Add Custom Item
                        </Button>
                      )}

                      <Divider sx={{ my: 1.5 }} />
                      <BillTotals totals={currentOpdTotals} />

                      <Box sx={{ mt: 2 }}>
                        <DiscountField value={billDiscount} onChange={setBillDiscount}
                          discountType={discountType} onTypeChange={setDiscountType} />
                        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                          <InputLabel>Payment Method</InputLabel>
                          <Select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} label="Payment Method">
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="card">Card</MenuItem>
                            <MenuItem value="upi">UPI</MenuItem>
                            <MenuItem value="online">Online Transfer</MenuItem>
                          </Select>
                        </FormControl>
                        {paymentMethod !== 'cash' && (
                          <TextField fullWidth size="small" label="Transaction / Reference ID"
                            placeholder="UPI ref, card approval code, NEFT/IMPS ref…"
                            value={billTransactionId} onChange={e => setBillTransactionId(e.target.value)}
                            sx={{ mb: 1.5 }} />
                        )}
                        <TextField fullWidth size="small" label="Notes (optional)" value={billNotes}
                          onChange={e => setBillNotes(e.target.value)} multiline rows={2} sx={{ mb: 1.5 }} />
                        <Button fullWidth variant="contained" size="large"
                          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Receipt />}
                          onClick={handleGenerateBill}
                          disabled={loading || (!walkinMode && !selectedPatient) || (walkinMode && !walkinName.trim()) || (!currentOpdItems.length && !!consultationInfo?.waived)}>
                          Generate Invoice
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {/* ── IPD TAB ── */}
              {generateTab === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Select IPD Admission</Typography>
                      <Autocomplete
                        options={activeAdmissions}
                        getOptionLabel={o => `${o.first_name} ${o.last_name} — ${o.room_number} (${o.ipd_id || o.id})`}
                        onChange={(_, v) => handleSelectAdmission(v)}
                        renderInput={params => <TextField {...params} label="Search active admissions" />}
                      />
                    </Paper>
                    {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 3 }} />}
                    {ipdBillingData && !loading && (
                      <Paper sx={{ p: 2 }}>
                        <Grid container spacing={1} sx={{ mb: 1 }}>
                          <Grid item xs={6}>
                            <Typography variant="body2"><strong>Patient:</strong> {ipdBillingData.admission.first_name} {ipdBillingData.admission.last_name}</Typography>
                            <Typography variant="body2"><strong>Room:</strong> {ipdBillingData.admission.room_number} ({ipdBillingData.admission.category_name})</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2"><strong>Admitted:</strong> {new Date(ipdBillingData.admission.admission_date).toLocaleDateString('en-IN')}</Typography>
                            <Typography variant="body2"><strong>Days:</strong> {ipdBillingData.admission.days_admitted}</Typography>
                          </Grid>
                        </Grid>
                        {ipdBillingData.prescribedPackages.length > 0 && (
                          <Alert severity="info" sx={{ mb: 1 }}>Package: {ipdBillingData.prescribedPackages.map(p => p.name).join(', ')} — included therapies are ₹0</Alert>
                        )}
                        {ipdBillingData.chargeableTherapies.length > 0 && (
                          <Alert severity="warning">{ipdBillingData.chargeableTherapies.length} therapy(ies) outside package — charged separately</Alert>
                        )}
                      </Paper>
                    )}
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 2, position: 'sticky', top: 20 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>IPD Invoice Summary</Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell><strong>Description</strong></TableCell>
                              <TableCell align="right"><strong>Qty</strong></TableCell>
                              <TableCell align="right"><strong>Rate</strong></TableCell>
                              <TableCell align="right"><strong>Total</strong></TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ipdBillItems.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ maxWidth: 140, wordBreak: 'break-word', fontSize: 12 }}>{item.description}</TableCell>
                                <TableCell align="right">{item.quantity}</TableCell>
                                <TableCell align="right">₹{item.unit_price}</TableCell>
                                <TableCell align="right">₹{item.total_amount.toFixed(2)}</TableCell>
                                <TableCell>
                                  <IconButton size="small" onClick={() => setIpdBillItems(prev => prev.filter((_, i) => i !== idx))}>
                                    <Delete fontSize="small" color="error" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                            {!ipdBillItems.length && (
                              <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="text.secondary">Select an admission</Typography></TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {showCustomForm ? (
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px dashed grey' }}>
                          <Typography variant="caption" fontWeight="bold" display="block" mb={1}>Custom Item</Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <TextField fullWidth size="small" label="Description *" value={customItemForm.desc}
                                onChange={e => setCustomItemForm(f => ({ ...f, desc: e.target.value }))} />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField fullWidth size="small" label="Qty" type="number" value={customItemForm.qty}
                                onChange={e => setCustomItemForm(f => ({ ...f, qty: e.target.value }))} inputProps={{ min: 1 }} />
                            </Grid>
                            <Grid item xs={5}>
                              <TextField fullWidth size="small" label="Unit Price ₹ *" type="number" value={customItemForm.price}
                                onChange={e => setCustomItemForm(f => ({ ...f, price: e.target.value }))} />
                            </Grid>
                            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                              <FormControlLabel control={<Switch size="small" checked={customItemForm.taxable}
                                onChange={e => setCustomItemForm(f => ({ ...f, taxable: e.target.checked }))} />}
                                label={<Typography variant="caption">GST</Typography>} sx={{ m: 0 }} />
                            </Grid>
                          </Grid>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button size="small" variant="contained" onClick={() => addCustomItem(setIpdBillItems)}>Add</Button>
                            <Button size="small" onClick={() => setShowCustomForm(false)}>Cancel</Button>
                          </Box>
                        </Box>
                      ) : (
                        <Button size="small" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => setShowCustomForm(true)}>
                          Add Custom Item
                        </Button>
                      )}

                      <Divider sx={{ my: 1.5 }} />
                      <BillTotals totals={calcTotals(ipdBillItems, parseFloat(billDiscount) || 0, discountType)} />
                      <Box sx={{ mt: 2 }}>
                        <DiscountField value={billDiscount} onChange={setBillDiscount}
                          discountType={discountType} onTypeChange={setDiscountType} />
                        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                          <InputLabel>Payment Method</InputLabel>
                          <Select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} label="Payment Method">
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="card">Card</MenuItem>
                            <MenuItem value="upi">UPI</MenuItem>
                            <MenuItem value="online">Online Transfer</MenuItem>
                          </Select>
                        </FormControl>
                        {paymentMethod !== 'cash' && (
                          <TextField fullWidth size="small" label="Transaction / Reference ID"
                            placeholder="UPI ref, card approval code, NEFT/IMPS ref…"
                            value={billTransactionId} onChange={e => setBillTransactionId(e.target.value)}
                            sx={{ mb: 1.5 }} />
                        )}
                        <TextField fullWidth size="small" label="Initial Payment (₹)" type="number"
                          value={initialPayment} onChange={e => setInitialPayment(e.target.value)}
                          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                          helperText="Leave blank to pay later"
                          sx={{ mb: 1.5 }} inputProps={{ min: 0 }} />
                        <TextField fullWidth size="small" label="Notes" value={billNotes}
                          onChange={e => setBillNotes(e.target.value)} multiline rows={2} sx={{ mb: 1.5 }} />
                        <Button fullWidth variant="contained" size="large"
                          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Receipt />}
                          onClick={handleGenerateBill} disabled={loading || !selectedAdmission || !ipdBillItems.length}>
                          Generate IPD Invoice
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {/* ── Day Care TAB ── */}
              {generateTab === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 2, mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Select Day Care Registration</Typography>
                      <Autocomplete
                        options={activeDayCareRegs}
                        getOptionLabel={o => `${o.first_name} ${o.last_name} — ${o.patient_code} (Start: ${new Date(o.start_date).toLocaleDateString('en-IN')})`}
                        onChange={(_, v) => handleSelectDayCare(v)}
                        renderInput={params => <TextField {...params} label="Search active day care registrations" />}
                      />
                    </Paper>
                    {selectedDayCare && (
                      <Paper sx={{ p: 2 }}>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="body2"><strong>Patient:</strong> {selectedDayCare.first_name} {selectedDayCare.last_name}</Typography>
                            <Typography variant="body2"><strong>Start Date:</strong> {new Date(selectedDayCare.start_date).toLocaleDateString('en-IN')}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2"><strong>Days Attended:</strong> {selectedDayCare.days_attended} / {selectedDayCare.planned_days}</Typography>
                            <Typography variant="body2"><strong>Daily Charge:</strong> ₹{parseFloat(selectedDayCare.daily_charge).toFixed(0)}/day</Typography>
                          </Grid>
                        </Grid>
                        <Alert severity="info" sx={{ mt: 1 }}>
                          Full Package: {selectedDayCare.planned_days} days × ₹{parseFloat(selectedDayCare.daily_charge).toFixed(0)} = <strong>₹{(parseFloat(selectedDayCare.daily_charge) * parseInt(selectedDayCare.planned_days)).toFixed(2)}</strong>
                        </Alert>
                      </Paper>
                    )}
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 2, position: 'sticky', top: 20 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Day Care Invoice Summary</Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell><strong>Description</strong></TableCell>
                              <TableCell align="right"><strong>Qty</strong></TableCell>
                              <TableCell align="right"><strong>Rate</strong></TableCell>
                              <TableCell align="right"><strong>Total</strong></TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dayCareItems.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell sx={{ fontSize: 12 }}>{item.description}</TableCell>
                                <TableCell align="right">{item.quantity}</TableCell>
                                <TableCell align="right">₹{item.unit_price}</TableCell>
                                <TableCell align="right">₹{item.total_amount.toFixed(2)}</TableCell>
                                <TableCell>
                                  <IconButton size="small" onClick={() => setDayCareItems(prev => prev.filter((_, i) => i !== idx))}>
                                    <Delete fontSize="small" color="error" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                            {!dayCareItems.length && (
                              <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="text.secondary">Select a registration</Typography></TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {showCustomForm ? (
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px dashed grey' }}>
                          <Typography variant="caption" fontWeight="bold" display="block" mb={1}>Custom Item</Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <TextField fullWidth size="small" label="Description *" value={customItemForm.desc}
                                onChange={e => setCustomItemForm(f => ({ ...f, desc: e.target.value }))} />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField fullWidth size="small" label="Qty" type="number" value={customItemForm.qty}
                                onChange={e => setCustomItemForm(f => ({ ...f, qty: e.target.value }))} inputProps={{ min: 1 }} />
                            </Grid>
                            <Grid item xs={5}>
                              <TextField fullWidth size="small" label="Unit Price ₹ *" type="number" value={customItemForm.price}
                                onChange={e => setCustomItemForm(f => ({ ...f, price: e.target.value }))} />
                            </Grid>
                            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                              <FormControlLabel control={<Switch size="small" checked={customItemForm.taxable}
                                onChange={e => setCustomItemForm(f => ({ ...f, taxable: e.target.checked }))} />}
                                label={<Typography variant="caption">GST</Typography>} sx={{ m: 0 }} />
                            </Grid>
                          </Grid>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button size="small" variant="contained" onClick={() => addCustomItem(setDayCareItems)}>Add</Button>
                            <Button size="small" onClick={() => setShowCustomForm(false)}>Cancel</Button>
                          </Box>
                        </Box>
                      ) : (
                        <Button size="small" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => setShowCustomForm(true)}>
                          Add Custom Item
                        </Button>
                      )}

                      <Divider sx={{ my: 1.5 }} />
                      <BillTotals totals={calcTotals(dayCareItems, parseFloat(billDiscount) || 0, discountType)} />
                      <Box sx={{ mt: 2 }}>
                        <DiscountField value={billDiscount} onChange={setBillDiscount}
                          discountType={discountType} onTypeChange={setDiscountType} />
                        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                          <InputLabel>Payment Method</InputLabel>
                          <Select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} label="Payment Method">
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="card">Card</MenuItem>
                            <MenuItem value="upi">UPI</MenuItem>
                            <MenuItem value="online">Online Transfer</MenuItem>
                          </Select>
                        </FormControl>
                        {paymentMethod !== 'cash' && (
                          <TextField fullWidth size="small" label="Transaction / Reference ID"
                            placeholder="UPI ref, card approval code, NEFT/IMPS ref…"
                            value={billTransactionId} onChange={e => setBillTransactionId(e.target.value)}
                            sx={{ mb: 1.5 }} />
                        )}
                        {/* Advance Days */}
                        {selectedDayCare && (
                          <Box sx={{ mb: 1.5 }}>
                            <TextField
                              fullWidth size="small"
                              label="Pay for how many days (advance)"
                              type="number"
                              value={dayCareAdvanceDays}
                              onChange={e => {
                                const days = e.target.value;
                                setDayCareAdvanceDays(days);
                                const parsedDays = parseInt(days) || 0;
                                const dailyCharge = parseFloat(selectedDayCare.daily_charge) || 0;
                                const totalAmount = calcTotals(dayCareItems, parseFloat(billDiscount) || 0, discountType).total;
                                if (parsedDays > 0) {
                                  const advAmt = Math.min(parsedDays * dailyCharge, totalAmount);
                                  setInitialPayment(advAmt.toFixed(2));
                                } else {
                                  setInitialPayment('');
                                }
                              }}
                              inputProps={{ min: 1, max: selectedDayCare.planned_days }}
                              helperText={
                                dayCareAdvanceDays && parseInt(dayCareAdvanceDays) > 0
                                  ? `Advance: ₹${Math.min(parseInt(dayCareAdvanceDays) * parseFloat(selectedDayCare.daily_charge), calcTotals(dayCareItems, parseFloat(billDiscount) || 0, discountType).total).toFixed(2)} — remaining balance due later`
                                  : `Enter days to auto-fill advance amount (max ${selectedDayCare.planned_days} days)`
                              }
                            />
                          </Box>
                        )}
                        <TextField fullWidth size="small" label="Advance Payment (₹)" type="number"
                          value={initialPayment} onChange={e => { setInitialPayment(e.target.value); setDayCareAdvanceDays(''); }}
                          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                          helperText={
                            initialPayment && selectedDayCare
                              ? `Remaining: ₹${Math.max(0, calcTotals(dayCareItems, parseFloat(billDiscount) || 0, discountType).total - (parseFloat(initialPayment) || 0)).toFixed(2)} due later`
                              : 'Leave blank to mark full amount as pending'
                          }
                          sx={{ mb: 1.5 }} inputProps={{ min: 0 }} />
                        <TextField fullWidth size="small" label="Notes" value={billNotes}
                          onChange={e => setBillNotes(e.target.value)} multiline rows={2} sx={{ mb: 1.5 }} />
                        <Button fullWidth variant="contained" color="secondary" size="large"
                          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Receipt />}
                          onClick={handleGenerateBill} disabled={loading || !selectedDayCare || !dayCareItems.length}>
                          Generate Day Care Invoice
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {/* All Invoices list */}
          {invoiceTab === allInvoicesTabIdx && (
            <Box>
              {/* Filters */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Search"
                      placeholder="Patient name, UHID, bill no."
                      value={billFilters.search}
                      onChange={e => setBillFilters(f => ({ ...f, search: e.target.value }))}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select value={billFilters.billType} onChange={e => setBillFilters(f => ({ ...f, billType: e.target.value }))} label="Type">
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="opd">OPD</MenuItem>
                        <MenuItem value="ipd">IPD</MenuItem>
                        <MenuItem value="day_care">Day Care</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select value={billFilters.paymentStatus} onChange={e => setBillFilters(f => ({ ...f, paymentStatus: e.target.value }))} label="Status">
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="partial">Partial</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth size="small" type="date" label="From"
                      value={billFilters.dateFrom} onChange={e => setBillFilters(f => ({ ...f, dateFrom: e.target.value }))} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth size="small" type="date" label="To"
                      value={billFilters.dateTo} onChange={e => setBillFilters(f => ({ ...f, dateTo: e.target.value }))} InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <Button fullWidth variant="contained" onClick={loadAllBills} disabled={allBillsLoading}>Search</Button>
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <Button fullWidth variant="outlined" startIcon={<Print />} onClick={handlePrintInvoiceSummary} disabled={allBills.length === 0}>
                      Print
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {allBillsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Invoice No.</strong></TableCell>
                        <TableCell><strong>Patient</strong></TableCell>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell align="right"><strong>Total</strong></TableCell>
                        <TableCell align="right"><strong>Paid</strong></TableCell>
                        <TableCell align="right"><strong>Refunded</strong></TableCell>
                        <TableCell align="right"><strong>Balance</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell align="center"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allBills.length === 0 ? (
                        <TableRow><TableCell colSpan={10} align="center">
                          <Typography color="text.secondary" sx={{ py: 3 }}>No invoices found</Typography>
                        </TableCell></TableRow>
                      ) : allBills.map(bill => {
                        const refunded = parseFloat(bill.amount_refunded || 0);
                        const balance = parseFloat(bill.total_amount) - parseFloat(bill.amount_paid || 0);
                        return (
                          <TableRow key={bill.id} hover>
                            <TableCell><Typography variant="body2" fontWeight="medium">{bill.bill_number}</Typography></TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">{bill.first_name} {bill.last_name}</Typography>
                              <Typography variant="caption" color="text.secondary">{bill.patient_code || bill.walkin_phone}</Typography>
                            </TableCell>
                            <TableCell>{billTypeChip(bill.bill_type)}</TableCell>
                            <TableCell><Typography variant="body2">{new Date(bill.bill_date).toLocaleDateString('en-IN')}</Typography></TableCell>
                            <TableCell align="right"><strong>₹{parseFloat(bill.total_amount).toFixed(2)}</strong></TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>₹{parseFloat(bill.amount_paid || 0).toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ color: refunded > 0 ? 'error.main' : 'text.disabled' }}>
                              {refunded > 0 ? `- ₹${refunded.toFixed(2)}` : '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ color: balance > 0 ? 'error.main' : 'success.main', fontWeight: 'bold' }}>
                              ₹{balance.toFixed(2)}
                            </TableCell>
                            <TableCell>{paymentStatusChip(bill.payment_status)}</TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <Tooltip title="View / Print Invoice">
                                  <IconButton size="small" color="primary" onClick={() => handleViewBill(bill.id)}>
                                    <Print fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit Invoice">
                                  <IconButton size="small" color="warning" onClick={() => openEditBill(bill)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {bill.payment_status !== 'paid' && (
                                  <Tooltip title="Record Payment">
                                    <IconButton size="small" color="success" onClick={() => openRecordPayment(bill)}>
                                      <Payments fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {(bill.payment_status === 'paid' || bill.payment_status === 'partial') && (
                                  <Tooltip title="Process Refund">
                                    <IconButton size="small" color="error"
                                      onClick={() => {
                                        const refundable = parseFloat(bill.amount_paid || 0) - parseFloat(bill.amount_refunded || 0);
                                        setRefundDialog({ open: true, bill });
                                        setRefundAmount(refundable.toFixed(2));
                                        setRefundMethod('cash'); setRefundReason('');
                                      }}>
                                      <Undo fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* ═══════════════════════ PAYMENTS SECTION ═══════════════════════ */}
      {section === 1 && (
        <Box>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                <Typography variant="caption" color="text.secondary">Today's Net Collection</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.dark">
                  ₹{parseFloat(paymentSummary.today_total || 0).toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary">{paymentSummary.today_count} payment(s)</Typography>
              </Paper>
            </Grid>
            {parseFloat(paymentSummary.today_refunds || 0) > 0 && (
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
                  <Typography variant="caption" color="text.secondary">Today's Refunds</Typography>
                  <Typography variant="h5" fontWeight="bold" color="error.dark">
                    ₹{parseFloat(paymentSummary.today_refunds || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{paymentSummary.today_refund_count || 0} refund(s)</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>

          {/* Filters */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" label="Search"
                  placeholder="Patient, receipt no., bill no."
                  value={paymentFilters.search}
                  onChange={e => setPaymentFilters(f => ({ ...f, search: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Method</InputLabel>
                  <Select value={paymentFilters.paymentMethod} onChange={e => setPaymentFilters(f => ({ ...f, paymentMethod: e.target.value }))} label="Method">
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="card">Card</MenuItem>
                    <MenuItem value="upi">UPI</MenuItem>
                    <MenuItem value="online">Online</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth size="small" type="date" label="From"
                  value={paymentFilters.dateFrom} onChange={e => setPaymentFilters(f => ({ ...f, dateFrom: e.target.value }))} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth size="small" type="date" label="To"
                  value={paymentFilters.dateTo} onChange={e => setPaymentFilters(f => ({ ...f, dateTo: e.target.value }))} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={3} md={1}>
                <Button fullWidth variant="contained" onClick={loadAllPayments} disabled={allPaymentsLoading}>Search</Button>
              </Grid>
              <Grid item xs={3} md={1}>
                <Button fullWidth variant="outlined" startIcon={<Print />} onClick={handlePrintPaymentSummary} disabled={allPayments.length === 0}>
                  Print
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {allPaymentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>Transaction No.</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Invoice No.</strong></TableCell>
                    <TableCell><strong>Patient</strong></TableCell>
                    <TableCell><strong>Bill Type</strong></TableCell>
                    <TableCell><strong>Date & Time</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell><strong>Method / Txn ID</strong></TableCell>
                    <TableCell><strong>Received By</strong></TableCell>
                    <TableCell><strong>Invoice Status</strong></TableCell>
                    <TableCell align="center"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allPayments.length === 0 ? (
                    <TableRow><TableCell colSpan={10} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>No transactions found</Typography>
                    </TableCell></TableRow>
                  ) : allPayments.map((p, idx) => {
                    const isRefund = p.transaction_type === 'refund';
                    const txDate = new Date(p.transaction_date);
                    return (
                      <TableRow key={`${p.transaction_type}-${p.id}-${idx}`} hover
                        sx={isRefund ? { bgcolor: 'error.50' } : {}}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium"
                            color={isRefund ? 'error.dark' : 'success.dark'}>
                            {p.transaction_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={isRefund ? 'REFUND' : 'PAYMENT'}
                            size="small"
                            color={isRefund ? 'error' : 'success'}
                            variant={isRefund ? 'filled' : 'outlined'}
                            icon={isRefund ? <Undo sx={{ fontSize: '14px !important' }} /> : undefined}
                          />
                        </TableCell>
                        <TableCell><Typography variant="body2">{p.bill_number}</Typography></TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">{p.first_name} {p.last_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{p.patient_code}</Typography>
                        </TableCell>
                        <TableCell>{billTypeChip(p.bill_type)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{txDate.toLocaleDateString('en-IN')}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {txDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color={isRefund ? 'error.dark' : 'success.dark'}>
                            {isRefund ? '- ' : ''}₹{parseFloat(p.amount).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={p.method?.toUpperCase()} size="small" variant="outlined" />
                          {p.transaction_id && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3 }}>
                              {p.transaction_id}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{p.staff_name || '—'}</Typography>
                        </TableCell>
                        <TableCell>{paymentStatusChip(p.payment_status)}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'center' }}>
                            {!isRefund && (
                              <>
                                <Tooltip title="Print Receipt">
                                  <IconButton size="small" color="primary"
                                    onClick={async () => {
                                      try {
                                        const res = await billingAPI.getBillWithPayments(p.bill_id);
                                        const bill = res.data.data;
                                        const payment = bill.payments?.find(pay => pay.transaction_number === p.transaction_number || pay.payment_number === p.transaction_number) || p;
                                        setPaymentDialog({ open: true, bill, payment });
                                      } catch { showSnackbar('Failed to load receipt', 'error'); }
                                    }}>
                                    <Print fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit Payment">
                                  <IconButton size="small" color="warning"
                                    onClick={() => openEditPayment(p, null)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {isRefund && (
                              <Tooltip title="View Invoice">
                                <IconButton size="small" color="error"
                                  onClick={() => handleViewBill(p.bill_id)}>
                                  <Receipt fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── Bill/Invoice print dialog ────────────────────────────────────────── */}
      <Dialog open={billDialog.open} onClose={() => setBillDialog({ open: false, bill: null })} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Invoice — {billDialog.bill?.bill_number}</span>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {billDialog.bill?.payment_status !== 'paid' && (
              <Button size="small" variant="outlined" color="success"
                startIcon={<Payments />}
                onClick={() => { setBillDialog({ open: false, bill: null }); openRecordPayment(billDialog.bill); }}>
                Record Payment
              </Button>
            )}
            <Button size="small" variant="contained" startIcon={<Print />} onClick={handlePrintBill}>Print</Button>
            <IconButton size="small" onClick={() => setBillDialog({ open: false, bill: null })}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {billDialog.bill && (
            <>
              {/* Payment status summary */}
              <Box sx={{ mb: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
                {paymentStatusChip(billDialog.bill.payment_status)}
                <Typography variant="body2">
                  Paid: <strong>₹{parseFloat(billDialog.bill.amount_paid || 0).toFixed(2)}</strong> &nbsp;|&nbsp;
                  Balance: <strong style={{ color: parseFloat(billDialog.bill.total_amount) - parseFloat(billDialog.bill.amount_paid || 0) > 0 ? '#d32f2f' : '#2e7d32' }}>
                    ₹{(parseFloat(billDialog.bill.total_amount) - parseFloat(billDialog.bill.amount_paid || 0)).toFixed(2)}
                  </strong>
                </Typography>
              </Box>
              <Box ref={billPrintRef}>
                <BillPrint bill={billDialog.bill} />
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Payment Receipt dialog ───────────────────────────────────────────── */}
      <Dialog open={paymentDialog.open} onClose={() => setPaymentDialog({ open: false, bill: null, payment: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Payment Receipt — {paymentDialog.payment?.payment_number}</span>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" startIcon={<Print />} onClick={handlePrintReceipt}>Print</Button>
            <IconButton size="small" onClick={() => setPaymentDialog({ open: false, bill: null, payment: null })}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {paymentDialog.bill && paymentDialog.payment && (
            <Box ref={receiptPrintRef}>
              <PaymentReceipt bill={paymentDialog.bill} payment={paymentDialog.payment} />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Record Payment dialog ────────────────────────────────────────────── */}
      <Dialog open={recordPaymentDialog.open} onClose={() => !paymentLoading && setRecordPaymentDialog({ open: false, bill: null })} maxWidth="xs" fullWidth>
        <DialogTitle>
          Record Payment
          {recordPaymentDialog.bill && (
            <Typography variant="caption" display="block" color="text.secondary">
              {recordPaymentDialog.bill.bill_number} — {recordPaymentDialog.bill.first_name} {recordPaymentDialog.bill.last_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {recordPaymentDialog.bill && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Invoice Total: <strong>₹{parseFloat(recordPaymentDialog.bill.total_amount).toFixed(2)}</strong> &nbsp;|&nbsp;
                Balance Due: <strong>₹{(parseFloat(recordPaymentDialog.bill.total_amount) - parseFloat(recordPaymentDialog.bill.amount_paid || 0)).toFixed(2)}</strong>
              </Alert>
              <TextField fullWidth label="Amount" type="number"
                value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                sx={{ mb: 2 }} />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Payment Method</InputLabel>
                <Select value={paymentMethodRec} onChange={e => setPaymentMethodRec(e.target.value)} label="Payment Method">
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="online">Online Transfer</MenuItem>
                </Select>
              </FormControl>
              {paymentMethodRec !== 'cash' && (
                <TextField fullWidth label="Transaction / Reference ID" placeholder="e.g. UPI ref, card approval code"
                  value={paymentTransactionId} onChange={e => setPaymentTransactionId(e.target.value)}
                  sx={{ mb: 2 }} />
              )}
              <TextField fullWidth label="Notes (optional)" multiline rows={2}
                value={paymentNotes} onChange={e => setPaymentNotes(e.target.value)} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordPaymentDialog({ open: false, bill: null })} disabled={paymentLoading}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleRecordPayment} disabled={paymentLoading}
            startIcon={paymentLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}>
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Refund Dialog ────────────────────────────────────────────────────── */}
      <Dialog open={refundDialog.open} onClose={() => !refundLoading && setRefundDialog({ open: false, bill: null })} maxWidth="xs" fullWidth>
        <DialogTitle>
          Process Refund
          {refundDialog.bill && (
            <Typography variant="caption" display="block" color="text.secondary">
              {refundDialog.bill.bill_number} — {refundDialog.bill.first_name} {refundDialog.bill.last_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {refundDialog.bill && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Amount Paid: <strong>₹{parseFloat(refundDialog.bill.amount_paid || 0).toFixed(2)}</strong>
                {parseFloat(refundDialog.bill.amount_refunded || 0) > 0 && (
                  <> &nbsp;|&nbsp; Already Refunded: <strong>₹{parseFloat(refundDialog.bill.amount_refunded).toFixed(2)}</strong></>
                )}
                &nbsp;|&nbsp; Refundable: <strong>₹{(parseFloat(refundDialog.bill.amount_paid || 0) - parseFloat(refundDialog.bill.amount_refunded || 0)).toFixed(2)}</strong>
              </Alert>
              <TextField fullWidth label="Refund Amount" type="number"
                value={refundAmount} onChange={e => setRefundAmount(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                sx={{ mb: 2 }} />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Refund Method</InputLabel>
                <Select value={refundMethod} onChange={e => setRefundMethod(e.target.value)} label="Refund Method">
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="online">Online Transfer</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth label="Reason" multiline rows={2}
                value={refundReason} onChange={e => setRefundReason(e.target.value)} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialog({ open: false, bill: null })} disabled={refundLoading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleProcessRefund} disabled={refundLoading}
            startIcon={refundLoading ? <CircularProgress size={16} color="inherit" /> : <Undo />}>
            Process Refund
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Bill dialog ─────────────────────────────────────────────────── */}
      <Dialog open={editBillDialog.open} onClose={() => !editBillLoading && setEditBillDialog({ open: false, bill: null })} maxWidth="xs" fullWidth>
        <DialogTitle>
          Edit Invoice
          {editBillDialog.bill && (
            <Typography variant="caption" display="block" color="text.secondary">
              {editBillDialog.bill.bill_number}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {editBillDialog.bill && (
            <Box sx={{ pt: 1 }}>
              {/* Walk-in guest details */}
              {(!editBillDialog.bill.patient_id || editBillDialog.bill.walkin_name) && (
                <>
                  <TextField fullWidth label="Guest Name" value={editBillWalkinName}
                    onChange={e => setEditBillWalkinName(e.target.value)}
                    sx={{ mb: 2 }} placeholder="Walk-in patient name" />
                  <TextField fullWidth label="Guest Phone" value={editBillWalkinPhone}
                    onChange={e => setEditBillWalkinPhone(e.target.value)}
                    sx={{ mb: 2 }} placeholder="Phone number" />
                </>
              )}
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select value={editBillPaymentMethod} onChange={e => setEditBillPaymentMethod(e.target.value)} label="Payment Method">
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="online">Online Transfer</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBillDialog({ open: false, bill: null })} disabled={editBillLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleEditBill} disabled={editBillLoading}
            startIcon={editBillLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Payment dialog ──────────────────────────────────────────────── */}
      <Dialog open={editPaymentDialog.open} onClose={() => !editPayLoading && setEditPaymentDialog({ open: false, payment: null, bill: null })} maxWidth="xs" fullWidth>
        <DialogTitle>
          Edit Payment
          {editPaymentDialog.payment && (
            <Typography variant="caption" display="block" color="text.secondary">
              {editPaymentDialog.payment.transaction_number || editPaymentDialog.payment.payment_number}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {editPaymentDialog.payment && (
            <Box sx={{ pt: 1 }}>
              <TextField fullWidth label="Amount" type="number"
                value={editPayAmount} onChange={e => setEditPayAmount(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                sx={{ mb: 2 }} />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Payment Method</InputLabel>
                <Select value={editPayMethod} onChange={e => setEditPayMethod(e.target.value)} label="Payment Method">
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="online">Online Transfer</MenuItem>
                </Select>
              </FormControl>
              {editPayMethod !== 'cash' && (
                <TextField fullWidth label="Transaction / Reference ID"
                  value={editPayTxnId} onChange={e => setEditPayTxnId(e.target.value)}
                  placeholder="e.g. UPI ref, card approval code" />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPaymentDialog({ open: false, payment: null, bill: null })} disabled={editPayLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleEditPayment} disabled={editPayLoading}
            startIcon={editPayLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Off-screen print summaries */}
      <InvoiceSummaryPrint bills={allBills} filters={billFilters} printRef={invoiceSummaryRef} />
      <PaymentSummaryPrint payments={allPayments} filters={paymentFilters} summary={paymentSummary} printRef={paymentSummaryRef} />

      {/* Day Report dialog */}
      <Dialog open={dayReportDialog.open} onClose={() => setDayReportDialog(d => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Print Day-wise Billing Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a date to print all invoices and payment collections for that day.
          </Typography>
          <TextField
            fullWidth label="Date" type="date"
            value={dayReportDialog.date}
            onChange={e => setDayReportDialog(d => ({ ...d, date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDayReportDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button variant="contained" startIcon={dayReportLoading ? <CircularProgress size={16} color="inherit" /> : <Print />}
            onClick={fetchAndPrintDayReport} disabled={dayReportLoading}>
            {dayReportLoading ? 'Loading...' : 'Generate & Print'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Day report print portal */}
      {dayReportData && createPortal(
        <div style={{ position: 'fixed', top: 0, left: '-9999px', width: '270mm' }}>
          <div ref={dayReportRef} style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#111', background: 'white', padding: '8mm 10mm' }}>
            {/* Header */}
            <div style={{ textAlign: 'center', borderBottom: '2px solid #1b5e20', paddingBottom: 8, marginBottom: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: '#1b5e20' }}>MSG NATUROPATHY CENTER</div>
              <div style={{ fontSize: 9, color: '#444' }}>Sirsa, Haryana &nbsp;|&nbsp; +91 7082999551 / 7082999351</div>
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4 }}>DAY-WISE BILLING REPORT</div>
              <div style={{ fontSize: 10, color: '#555' }}>Date: {new Date(dayReportData.date).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</div>
              <div style={{ fontSize: 9, color: '#888' }}>Printed: {new Date().toLocaleString('en-IN')}</div>
            </div>

            {/* Summary cards */}
            {(() => {
              const bills = dayReportData.bills;
              const payments = dayReportData.payments;
              const totalBilled = bills.reduce((s, b) => s + parseFloat(b.total_amount || 0), 0);
              const totalCollected = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
              return (
                <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                  {[
                    { label: 'Invoices Generated', value: bills.length, color: '#1565c0' },
                    { label: 'Total Billed', value: `₹${totalBilled.toFixed(2)}`, color: '#1b5e20' },
                    { label: 'Payments Received', value: payments.length, color: '#6a1b9a' },
                    { label: 'Total Collected', value: `₹${totalCollected.toFixed(2)}`, color: '#2e7d32' },
                  ].map(c => (
                    <div key={c.label} style={{ flex: 1, border: `1.5px solid ${c.color}`, borderRadius: 4, padding: '5px 8px', textAlign: 'center' }}>
                      <div style={{ fontSize: 9, color: '#555' }}>{c.label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: c.color }}>{c.value}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Invoices table */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ background: '#e8f5e9', padding: '4px 8px', fontWeight: 700, fontSize: 11, borderLeft: '4px solid #2e7d32', marginBottom: 4 }}>
                Invoices ({dayReportData.bills.length})
              </div>
              {dayReportData.bills.length === 0 ? (
                <div style={{ color: '#888', fontSize: 10, padding: '6px 8px' }}>No invoices for this date.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      {['#', 'Invoice No.', 'Patient', 'UHID', 'Type', 'Total (₹)', 'Paid (₹)', 'Balance (₹)', 'Status'].map(h => (
                        <th key={h} style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: ['#', 'Total (₹)', 'Paid (₹)', 'Balance (₹)'].includes(h) ? 'center' : 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dayReportData.bills.map((b, i) => {
                      const bal = parseFloat(b.total_amount) - parseFloat(b.amount_paid || 0);
                      return (
                        <tr key={b.id} style={{ background: i%2===0?'#fff':'#fafafa' }}>
                          <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'center' }}>{i+1}</td>
                          <td style={{ border: '1px solid #ddd', padding: '3px 5px', fontWeight: 600 }}>{b.bill_number}</td>
                          <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{b.first_name} {b.last_name}</td>
                          <td style={{ border: '1px solid #ddd', padding: '3px 5px', color: '#555' }}>{b.patient_code}</td>
                          <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{b.bill_type?.toUpperCase()}</td>
                          <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'right', fontWeight: 600 }}>{parseFloat(b.total_amount).toFixed(2)}</td>
                          <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'right', color: '#2e7d32' }}>{parseFloat(b.amount_paid||0).toFixed(2)}</td>
                          <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'right', color: bal>0?'#c62828':'#2e7d32', fontWeight: 600 }}>{bal.toFixed(2)}</td>
                          <td style={{ border: '1px solid #ddd', padding: '3px 5px', color: b.payment_status==='paid'?'#2e7d32':b.payment_status==='partial'?'#e65100':'#c62828', fontWeight: 600 }}>{b.payment_status?.toUpperCase()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#e8f5e9', fontWeight: 700 }}>
                      <td colSpan={5} style={{ border: '1px solid #ccc', padding: '3px 5px', textAlign: 'right' }}>TOTAL</td>
                      <td style={{ border: '1px solid #ccc', padding: '3px 5px', textAlign: 'right' }}>{dayReportData.bills.reduce((s,b)=>s+parseFloat(b.total_amount||0),0).toFixed(2)}</td>
                      <td style={{ border: '1px solid #ccc', padding: '3px 5px', textAlign: 'right', color: '#2e7d32' }}>{dayReportData.bills.reduce((s,b)=>s+parseFloat(b.amount_paid||0),0).toFixed(2)}</td>
                      <td style={{ border: '1px solid #ccc', padding: '3px 5px', textAlign: 'right' }}>{dayReportData.bills.reduce((s,b)=>s+(parseFloat(b.total_amount||0)-parseFloat(b.amount_paid||0)),0).toFixed(2)}</td>
                      <td style={{ border: '1px solid #ccc' }}></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            {/* Payments table */}
            <div>
              <div style={{ background: '#e3f2fd', padding: '4px 8px', fontWeight: 700, fontSize: 11, borderLeft: '4px solid #1565c0', marginBottom: 4 }}>
                Payment Collections ({dayReportData.payments.length})
              </div>
              {dayReportData.payments.length === 0 ? (
                <div style={{ color: '#888', fontSize: 10, padding: '6px 8px' }}>No payments received on this date.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      {['#', 'Receipt No.', 'Patient', 'UHID', 'Invoice No.', 'Amount (₹)', 'Method', 'Time', 'Received By'].map(h => (
                        <th key={h} style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: h==='Amount (₹)'?'right':'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dayReportData.payments.map((p, i) => (
                      <tr key={p.id} style={{ background: i%2===0?'#fff':'#fafafa' }}>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'center' }}>{i+1}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px', fontWeight: 600 }}>{p.payment_number}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{p.first_name} {p.last_name}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px', color: '#555' }}>{p.patient_code}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{p.bill_number}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'right', fontWeight: 700, color: '#1b5e20' }}>{parseFloat(p.amount).toFixed(2)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{p.payment_method?.toUpperCase()}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{new Date(p.payment_date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{p.received_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#e3f2fd', fontWeight: 700 }}>
                      <td colSpan={5} style={{ border: '1px solid #ccc', padding: '3px 5px', textAlign: 'right' }}>TOTAL COLLECTED</td>
                      <td style={{ border: '1px solid #ccc', padding: '3px 5px', textAlign: 'right', color: '#1b5e20' }}>{dayReportData.payments.reduce((s,p)=>s+parseFloat(p.amount||0),0).toFixed(2)}</td>
                      <td colSpan={3} style={{ border: '1px solid #ccc' }}></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </Box>
  );
}

/* ══════════════════════════════════════════════════════════════
   INVOICE SUMMARY PRINT CONTENT (rendered off-screen via portal)
══════════════════════════════════════════════════════════════ */
function InvoiceSummaryPrint({ bills, filters, printRef }) {
  const totalAmount = bills.reduce((s, b) => s + parseFloat(b.total_amount || 0), 0);
  const totalPaid   = bills.reduce((s, b) => s + parseFloat(b.amount_paid || 0), 0);
  const totalBal    = totalAmount - totalPaid;
  const now = new Date().toLocaleString('en-IN');
  const dateRange = filters.dateFrom || filters.dateTo
    ? `${filters.dateFrom || '—'} to ${filters.dateTo || '—'}`
    : 'All dates';

  return createPortal(
    <div style={{ position: 'fixed', top: 0, left: '-9999px', width: '270mm' }}>
      <div ref={printRef} style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#111', background: 'white', padding: '8mm 10mm' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', borderBottom: '2px solid #1b5e20', paddingBottom: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 16, fontWeight: 900, color: '#1b5e20' }}>MSG NATUROPATHY CENTER</div>
          <div style={{ fontSize: 11, color: '#444' }}>Sirsa, Haryana &nbsp;|&nbsp; +91 7082999551 / 7082999351</div>
          <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6 }}>INVOICE SUMMARY REPORT</div>
          <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>
            Period: {dateRange}
            {filters.billType ? `  |  Type: ${filters.billType.toUpperCase()}` : ''}
            {filters.paymentStatus ? `  |  Status: ${filters.paymentStatus.toUpperCase()}` : ''}
            {filters.search ? `  |  Search: "${filters.search}"` : ''}
          </div>
          <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>Printed: {now}</div>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          {[
            { label: 'Total Invoices', value: bills.length, color: '#1565c0' },
            { label: 'Total Amount', value: `₹${totalAmount.toFixed(2)}`, color: '#1b5e20' },
            { label: 'Total Collected', value: `₹${totalPaid.toFixed(2)}`, color: '#2e7d32' },
            { label: 'Total Balance', value: `₹${totalBal.toFixed(2)}`, color: totalBal > 0 ? '#c62828' : '#2e7d32' },
          ].map(c => (
            <div key={c.label} style={{ flex: 1, border: `1.5px solid ${c.color}`, borderRadius: 4, padding: '6px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 9, color: '#555' }}>{c.label}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>

        {/* Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
          <thead>
            <tr style={{ background: '#e8f5e9' }}>
              {['#', 'Invoice No.', 'Patient', 'UHID', 'Type', 'Date', 'Total (₹)', 'Paid (₹)', 'Balance (₹)', 'Status'].map(h => (
                <th key={h} style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: h === '#' || h === 'Type' || h === 'Status' ? 'center' : h.includes('₹') ? 'right' : 'left' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bills.map((bill, i) => {
              const bal = parseFloat(bill.total_amount) - parseFloat(bill.amount_paid || 0);
              const statusColors = { paid: '#2e7d32', partial: '#e65100', pending: '#c62828' };
              return (
                <tr key={bill.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                  <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'center' }}>{i + 1}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 5px', fontWeight: 600 }}>{bill.bill_number}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{bill.first_name} {bill.last_name}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 5px', color: '#555' }}>{bill.patient_code}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'center' }}>{bill.bill_type?.toUpperCase()}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{new Date(bill.bill_date).toLocaleDateString('en-IN')}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'right', fontWeight: 600 }}>{parseFloat(bill.total_amount).toFixed(2)}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'right', color: '#2e7d32' }}>{parseFloat(bill.amount_paid || 0).toFixed(2)}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'right', color: bal > 0 ? '#c62828' : '#2e7d32', fontWeight: 600 }}>{bal.toFixed(2)}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'center', color: statusColors[bill.payment_status] || '#333', fontWeight: 600 }}>
                    {bill.payment_status?.toUpperCase()}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: '#e8f5e9', fontWeight: 700 }}>
              <td colSpan={6} style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'right' }}>TOTAL ({bills.length} invoices)</td>
              <td style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'right' }}>{totalAmount.toFixed(2)}</td>
              <td style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'right', color: '#2e7d32' }}>{totalPaid.toFixed(2)}</td>
              <td style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'right', color: totalBal > 0 ? '#c62828' : '#2e7d32' }}>{totalBal.toFixed(2)}</td>
              <td style={{ border: '1px solid #ccc', padding: '4px 6px' }}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>,
    document.body
  );
}

/* ══════════════════════════════════════════════════════════════
   PAYMENT SUMMARY PRINT CONTENT (rendered off-screen via portal)
══════════════════════════════════════════════════════════════ */
function PaymentSummaryPrint({ payments, filters, summary, printRef }) {
  const totalCollected = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const byMethod = payments.reduce((acc, p) => {
    acc[p.payment_method] = (acc[p.payment_method] || 0) + parseFloat(p.amount || 0);
    return acc;
  }, {});
  const now = new Date().toLocaleString('en-IN');
  const dateRange = filters.dateFrom || filters.dateTo
    ? `${filters.dateFrom || '—'} to ${filters.dateTo || '—'}`
    : 'All dates';

  return createPortal(
    <div style={{ position: 'fixed', top: 0, left: '-9999px', width: '270mm' }}>
      <div ref={printRef} style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#111', background: 'white', padding: '8mm 10mm' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', borderBottom: '2px solid #1b5e20', paddingBottom: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 16, fontWeight: 900, color: '#1b5e20' }}>MSG NATUROPATHY CENTER</div>
          <div style={{ fontSize: 11, color: '#444' }}>Sirsa, Haryana &nbsp;|&nbsp; +91 7082999551 / 7082999351</div>
          <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6 }}>PAYMENT COLLECTION REPORT</div>
          <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>
            Period: {dateRange}
            {filters.paymentMethod ? `  |  Method: ${filters.paymentMethod.toUpperCase()}` : ''}
            {filters.search ? `  |  Search: "${filters.search}"` : ''}
          </div>
          <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>Printed: {now}</div>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1, border: '1.5px solid #1565c0', borderRadius: 4, padding: '6px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: 9, color: '#555' }}>Today's Collection</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1565c0' }}>₹{parseFloat(summary.today_total || 0).toFixed(2)}</div>
            <div style={{ fontSize: 9, color: '#777' }}>{summary.today_count} transaction(s)</div>
          </div>
          <div style={{ flex: 1, border: '1.5px solid #1b5e20', borderRadius: 4, padding: '6px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: 9, color: '#555' }}>Total in Report</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1b5e20' }}>₹{totalCollected.toFixed(2)}</div>
            <div style={{ fontSize: 9, color: '#777' }}>{payments.length} transaction(s)</div>
          </div>
          {Object.entries(byMethod).map(([method, amt]) => (
            <div key={method} style={{ flex: 1, border: '1.5px solid #555', borderRadius: 4, padding: '6px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 9, color: '#555' }}>{method.toUpperCase()}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>₹{amt.toFixed(2)}</div>
            </div>
          ))}
        </div>

        {/* Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
          <thead>
            <tr style={{ background: '#e8f5e9' }}>
              {['#', 'Receipt No.', 'Invoice No.', 'Patient', 'UHID', 'Type', 'Date & Time', 'Method', 'Amount (₹)', 'Invoice Status'].map(h => (
                <th key={h} style={{ border: '1px solid #ccc', padding: '4px 5px', textAlign: h === '#' || h === 'Type' || h === 'Method' || h === 'Invoice Status' ? 'center' : h.includes('₹') ? 'right' : 'left' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payments.map((p, i) => {
              const statusColors = { paid: '#2e7d32', partial: '#e65100', pending: '#c62828' };
              return (
                <tr key={p.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                  <td style={{ border: '1px solid #ddd', padding: '3px 4px', textAlign: 'center' }}>{i + 1}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 4px', fontWeight: 600, color: '#2e7d32' }}>{p.payment_number}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 4px' }}>{p.bill_number}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 4px' }}>{p.first_name} {p.last_name}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 4px', color: '#555' }}>{p.patient_code}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 4px', textAlign: 'center' }}>{p.bill_type?.toUpperCase()}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 4px' }}>
                    {new Date(p.payment_date).toLocaleDateString('en-IN')} {new Date(p.payment_date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 4px', textAlign: 'center' }}>{p.payment_method?.toUpperCase()}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 4px', textAlign: 'right', fontWeight: 700, color: '#2e7d32' }}>{parseFloat(p.amount).toFixed(2)}</td>
                  <td style={{ border: '1px solid #ddd', padding: '3px 4px', textAlign: 'center', color: statusColors[p.payment_status] || '#333', fontWeight: 600 }}>
                    {p.payment_status?.toUpperCase()}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: '#e8f5e9', fontWeight: 700 }}>
              <td colSpan={8} style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'right' }}>TOTAL ({payments.length} transactions)</td>
              <td style={{ border: '1px solid #ccc', padding: '4px 6px', textAlign: 'right', color: '#1b5e20' }}>{totalCollected.toFixed(2)}</td>
              <td style={{ border: '1px solid #ccc', padding: '4px 6px' }}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>,
    document.body
  );
}

export default BillingModule;
